import React from 'react';
import { Linking, TouchableOpacity, ViewProps, ViewStyle, TextStyle } from 'react-native';
import { Text } from './Text';

// Simplified Link component for debugging
interface LinkProps extends ViewProps {
  href?: string;
  isExternal?: boolean;
  isUnderlined?: boolean;
  onPress?: () => void;
  _text?: {
    color?: string;
    variant?: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontFamily?: string;
    textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify';
    textDecorationLine?: 'none' | 'underline' | 'line-through' | 'underline line-through';
  };
  color?: string;
  variant?: string;
  fontSize?: number;
  fontWeight?: string | number;
  fontFamily?: string;
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify';
  children?: React.ReactNode;
  style?: ViewStyle;
}

const Link: React.FC<LinkProps> = ({
  href,
  isExternal = false,
  isUnderlined = true,
  onPress,
  _text,
  color,
  variant,
  fontSize,
  fontWeight,
  fontFamily,
  textAlign,
  children,
  style,
  ...otherProps
}) => {
  // Merge _text props with direct props
  const textColor = _text?.color || color;
  const textVariant = _text?.variant || variant;
  const textFontSize = _text?.fontSize || fontSize;
  const textFontWeight = _text?.fontWeight || fontWeight;
  const textFontFamily = _text?.fontFamily || fontFamily;
  const textAlignValue = _text?.textAlign || textAlign;

  // Determine text decoration
  const textDecorationLine = isUnderlined ? 'underline' : 'none';

  // Handle text variant styling (simplified for React Native Text)
  const getVariantStyle = (variant?: string): TextStyle => {
    if (!variant) return {};

    // Map common variants to styles
    switch (variant) {
      case 'sm-normal':
        return { fontSize: 14, fontWeight: 'normal' };
      case 'sm-bold':
        return { fontSize: 14, fontWeight: 'bold' };
      case 'md-normal':
        return { fontSize: 16, fontWeight: 'normal' };
      case 'md-bold':
        return { fontSize: 16, fontWeight: 'bold' };
      case 'lg-normal':
        return { fontSize: 18, fontWeight: 'normal' };
      case 'lg-bold':
        return { fontSize: 18, fontWeight: 'bold' };
      default:
        return {};
    }
  };

  const handlePress = async () => {
    if (onPress) {
      onPress();
      return;
    }

    if (href) {
      try {
        if (isExternal) {
          // For external links, use Linking API
          const supported = await Linking.canOpenURL(href);
          if (supported) {
            await Linking.openURL(href);
          } else {
            console.warn(`Cannot open URL: ${href}`);
          }
        } else {
          // For internal links, you might want to use navigation
          // For now, we'll just use Linking as well
          const supported = await Linking.canOpenURL(href);
          if (supported) {
            await Linking.openURL(href);
          }
        }
      } catch (error) {
        console.error('Error opening link:', error);
      }
    }
  };

  const linkStyle: ViewStyle = {
    alignSelf: 'flex-start', // Allow link to size to content
  };

  const textStyle: TextStyle = {
    ...getVariantStyle(textVariant),
    color: textColor,
    fontSize: textFontSize,
    fontWeight: textFontWeight,
    fontFamily: textFontFamily,
    textAlign: textAlignValue,
    textDecorationLine,
  };

  return (
    <TouchableOpacity style={[linkStyle, style]} onPress={handlePress} activeOpacity={0.7} {...otherProps}>
      <Text variant={textVariant} style={textStyle}>
        {children}
      </Text>
    </TouchableOpacity>
  );
};

export default Link;
